import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { API_BASE_URL } from '../config';
import secureStorage from '../utils/secureStorage';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Badge } from './ui/badge';
import { Textarea } from './ui/textarea';
import { ChevronDown, ChevronUp, Plus, Trash2, Edit, Radio, User, Save, X, MapPin, Copy, Check } from 'lucide-react';
import { QRCodeSVG } from 'qrcode.react';

const API = `${API_BASE_URL}/api`;

const UNIT_STATUSES = [
  { value: 'Requested', color: 'bg-amber-500/20 text-amber-400 border-amber-500/30' },
  { value: 'Available', color: 'bg-green-500/20 text-green-400 border-green-500/30' },
  { value: 'Assigned', color: 'bg-blue-500/20 text-blue-400 border-blue-500/30' },
  { value: 'In Transit', color: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30' },
  { value: 'On Scene', color: 'bg-orange-500/20 text-orange-400 border-orange-500/30' },
  { value: 'Released', color: 'bg-gray-500/20 text-gray-400 border-gray-500/30' },
  { value: 'Unavailable', color: 'bg-red-500/20 text-red-400 border-red-500/30' },
];

const getStatusColor = (status) => UNIT_STATUSES.find(s => s.value === status)?.color || 'bg-gray-500/20 text-gray-400';

const DEFAULT_FORM = {
  unit_name: '',
  status: 'Available',
  personnel_needed: 0,
  notes: '',
  vehicle_licence_id: '',
  exercise_start_date: '',
  exercise_start_time: '',
  incident_release_date: '',
  incident_release_time: '',
  description: '',
  work_assignment_special_inst: '',
  overhead_position: '',
  special_equip_supplies: '',
  location: '',
  assigned_exercise: '',
  requested_arr_datetime: '',
  involved_in_exercise: false,
};

const ResourceUnits = ({ resourceId, resourceName, exerciseId = null, positions = [] }) => {
  const [units, setUnits] = useState([]);
  const [expanded, setExpanded] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [editingUnit, setEditingUnit] = useState(null);
  const [form, setForm] = useState({ ...DEFAULT_FORM });
  const [trackerModal, setTrackerModal] = useState(null);
  const [copiedField, setCopiedField] = useState(null);
  const [locations, setLocations] = useState([]);
  const [exercises, setExercises] = useState([]);

  const getHeaders = useCallback(() => {
    const token = secureStorage.getToken();
    return token ? { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' } : { 'Content-Type': 'application/json' };
  }, []);

  const fetchUnits = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/resources/${resourceId}/units`, { headers: getHeaders() });
      setUnits(res.data || []);
    } catch (err) {
      if (err.response?.status !== 404) console.error('Error loading units:', err);
    }
  }, [resourceId, getHeaders]);

  useEffect(() => {
    if (expanded && resourceId) fetchUnits();
  }, [expanded, resourceId, fetchUnits]);

  // Fetch locations (map markers) for location dropdown
  useEffect(() => {
    if (!expanded) return;
    const fetchLocations = async () => {
      try {
        const url = exerciseId
          ? `${API}/map-objects?type=marker&exercise_id=${exerciseId}`
          : `${API}/map-objects?type=marker`;
        const res = await axios.get(url);
        setLocations(res.data.map(m => m.name));
      } catch {
        setLocations([]);
      }
    };
    fetchLocations();
  }, [expanded, exerciseId]);

  // Fetch exercises for dropdown
  useEffect(() => {
    if (!expanded) return;
    const fetchExercises = async () => {
      try {
        const res = await axios.get(`${API_BASE_URL}/api/exercise-builder`, { headers: getHeaders() });
        setExercises(res.data || []);
      } catch {
        setExercises([]);
      }
    };
    fetchExercises();
  }, [expanded, getHeaders]);

  const defaultLocations = [
    'Command Post', 'Hospital', 'Fire Station', 'Police Station',
    'Operations Center EOC', 'Staging Area', 'Medical Aid Station',
    'Medical', 'Communications Center', 'Municipal Water Department',
    'Emergency Equipment Warehouse', 'Fire Hall', 'Stationary Unit'
  ];

  const locationOptions = locations.length > 0 ? locations : defaultLocations;

  const resetForm = () => {
    setForm({ ...DEFAULT_FORM });
    setEditingUnit(null);
    setShowForm(false);
  };

  const handleSave = async () => {
    if (!form.unit_name.trim()) {
      window.alert('Unit Name/ID is required');
      return;
    }
    try {
      if (editingUnit) {
        await axios.put(`${API}/resources/${resourceId}/units/${editingUnit.id}`, form, { headers: getHeaders() });
      } else {
        await axios.post(`${API}/resources/${resourceId}/units`, form, { headers: getHeaders() });
      }
      resetForm();
      fetchUnits();
    } catch (err) {
      console.error('Error saving unit:', err);
      window.alert('Failed to save unit');
    }
  };

  const handleEdit = (unit) => {
    setForm({
      unit_name: unit.unit_name || '',
      status: unit.status || 'Available',
      personnel_needed: unit.personnel_needed || 0,
      notes: unit.notes || '',
      vehicle_licence_id: unit.vehicle_licence_id || '',
      exercise_start_date: unit.exercise_start_date || '',
      exercise_start_time: unit.exercise_start_time || '',
      incident_release_date: unit.incident_release_date || '',
      incident_release_time: unit.incident_release_time || '',
      description: unit.description || '',
      work_assignment_special_inst: unit.work_assignment_special_inst || '',
      overhead_position: unit.overhead_position || '',
      special_equip_supplies: unit.special_equip_supplies || '',
      location: unit.location || '',
      assigned_exercise: unit.assigned_exercise || '',
      requested_arr_datetime: unit.requested_arr_datetime || '',
      involved_in_exercise: unit.involved_in_exercise || false,
    });
    setEditingUnit(unit);
    setShowForm(true);
  };

  const handleDelete = async (unitId) => {
    if (!window.confirm('Delete this unit?')) return;
    try {
      await axios.delete(`${API}/resources/${resourceId}/units/${unitId}`, { headers: getHeaders() });
      fetchUnits();
    } catch (err) {
      console.error('Error deleting unit:', err);
    }
  };

  const handleTracker = async (unit) => {
    if (unit.tracking_code) {
      const link = `${window.location.origin}/#tracker/${unit.tracking_code}`;
      setTrackerModal({ code: unit.tracking_code, name: unit.unit_name, link });
      return;
    }
    try {
      const res = await axios.post(
        `${API}/resources/${resourceId}/units/${unit.id}/generate-tracking-code`,
        {},
        { headers: getHeaders() }
      );
      const code = res.data.tracking_code;
      const link = `${window.location.origin}/#tracker/${code}`;
      setUnits(prev => prev.map(u => u.id === unit.id ? { ...u, tracking_code: code } : u));
      setTrackerModal({ code, name: unit.unit_name, link });
    } catch (err) {
      console.error('Error generating tracking code:', err);
      window.alert('Failed to generate tracking code');
    }
  };

  const copyToClipboard = (text, field) => {
    navigator.clipboard.writeText(text);
    setCopiedField(field);
    setTimeout(() => setCopiedField(null), 2000);
  };

  const handleTimeInput = (value, field) => {
    let v = value.replace(/[^0-9:]/g, '');
    if (v.length === 2 && !v.includes(':')) v = v + ':';
    if (v.length <= 5) setForm(prev => ({ ...prev, [field]: v }));
  };

  return (
    <div className="mt-2 border-t border-gray-700/50 pt-2" data-testid={`resource-units-${resourceId}`}>
      <button
        onClick={() => setExpanded(!expanded)}
        className="flex items-center gap-2 text-sm text-orange-400 hover:text-orange-300 transition-colors w-full"
        data-testid={`toggle-units-${resourceId}`}
      >
        {expanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
        <span className="font-medium">Individual Units</span>
        {units.length > 0 && <Badge className="bg-orange-500/20 text-orange-400 border-orange-500/30 text-xs">{units.length}</Badge>}
      </button>

      {expanded && (
        <div className="mt-2 space-y-2">
          {/* Unit list */}
          {units.length === 0 && !showForm && (
            <p className="text-gray-500 text-xs pl-6">No individual units registered yet</p>
          )}
          {units.map(unit => (
            <div key={unit.id} className="flex items-center justify-between bg-gray-700/40 rounded-lg px-3 py-2 ml-4">
              <div className="flex items-center gap-3 flex-1 min-w-0">
                <Radio className="h-3.5 w-3.5 text-orange-500 flex-shrink-0" />
                <span className="text-white text-sm font-medium truncate">{unit.unit_name}</span>
                <Badge className={`text-[10px] px-1.5 py-0 ${getStatusColor(unit.status)}`}>{unit.status}</Badge>
                {unit.personnel_needed > 0 && <span className="text-gray-400 text-xs flex items-center gap-1"><User className="h-3 w-3" />Personnel: {unit.personnel_needed}</span>}
                {unit.location && <span className="text-gray-400 text-xs flex items-center gap-1"><MapPin className="h-3 w-3" />{unit.location}</span>}
                {unit.tracking_code && <Badge className="bg-green-500/10 text-green-400 border-green-500/20 text-[10px] px-1.5 py-0">GPS Active</Badge>}
              </div>
              <div className="flex gap-1 flex-shrink-0">
                <button
                  onClick={() => handleTracker(unit)}
                  className={`p-1 transition-colors ${unit.tracking_code ? 'text-green-400 hover:text-green-300' : 'text-orange-400 hover:text-orange-300'}`}
                  title={unit.tracking_code ? 'View Tracker' : 'Generate Tracker'}
                  data-testid={`track-unit-${unit.id}`}
                >
                  <MapPin className="h-3.5 w-3.5" />
                </button>
                <button onClick={() => handleEdit(unit)} className="p-1 text-blue-400 hover:text-blue-300" data-testid={`edit-unit-${unit.id}`}>
                  <Edit className="h-3.5 w-3.5" />
                </button>
                <button onClick={() => handleDelete(unit.id)} className="p-1 text-red-400 hover:text-red-300" data-testid={`delete-unit-${unit.id}`}>
                  <Trash2 className="h-3.5 w-3.5" />
                </button>
              </div>
            </div>
          ))}

          {/* Add/Edit form */}
          {showForm ? (
            <div className="ml-4 bg-gray-800/60 rounded-lg p-3 space-y-3 border border-gray-700/50">
              {/* Row 1: Unit Name/ID, Status, # of Personnel */}
              <div className="grid grid-cols-3 gap-2">
                <div>
                  <label className="text-gray-400 text-xs">Unit Name/ID *</label>
                  <Input
                    value={form.unit_name}
                    onChange={(e) => setForm(prev => ({ ...prev, unit_name: e.target.value }))}
                    placeholder="e.g., Rescue-101"
                    className="bg-gray-700 border-gray-600 text-white h-8 text-sm"
                    data-testid="unit-name-input"
                  />
                </div>
                <div>
                  <label className="text-gray-400 text-xs">Status</label>
                  <select
                    value={form.status}
                    onChange={(e) => setForm(prev => ({ ...prev, status: e.target.value }))}
                    className="w-full bg-gray-700 border border-gray-600 text-white rounded-md h-8 text-sm px-2"
                    data-testid="unit-status-select"
                  >
                    {UNIT_STATUSES.map(s => <option key={s.value} value={s.value}>{s.value}</option>)}
                  </select>
                </div>
                <div>
                  <label className="text-gray-400 text-xs"># of Personnel</label>
                  <Input
                    type="number"
                    min="0"
                    value={form.personnel_needed}
                    onChange={(e) => setForm(prev => ({ ...prev, personnel_needed: parseInt(e.target.value) || 0 }))}
                    placeholder="0"
                    className="bg-gray-700 border-gray-600 text-white h-8 text-sm"
                    data-testid="unit-personnel-needed-input"
                  />
                </div>
              </div>

              {/* Row 2: Vehicle Licence, Location, Overhead Position */}
              <div className="grid grid-cols-3 gap-2">
                <div>
                  <label className="text-gray-400 text-xs">Vehicle Licence or ID #</label>
                  <Input
                    value={form.vehicle_licence_id}
                    onChange={(e) => setForm(prev => ({ ...prev, vehicle_licence_id: e.target.value }))}
                    placeholder="License plate or ID"
                    className="bg-gray-700 border-gray-600 text-white h-8 text-sm"
                    data-testid="unit-vehicle-licence-input"
                  />
                </div>
                <div>
                  <label className="text-gray-400 text-xs">Current/Assigned Location</label>
                  <select
                    value={form.location}
                    onChange={(e) => setForm(prev => ({ ...prev, location: e.target.value }))}
                    className="w-full bg-gray-700 border border-gray-600 text-white rounded-md h-8 text-sm px-2"
                    data-testid="unit-location-select"
                  >
                    <option value="">Select location</option>
                    {locationOptions.map(loc => <option key={loc} value={loc}>{loc}</option>)}
                  </select>
                </div>
                <div>
                  <label className="text-gray-400 text-xs">Overhead Position</label>
                  <select
                    value={form.overhead_position}
                    onChange={(e) => setForm(prev => ({ ...prev, overhead_position: e.target.value }))}
                    className="w-full bg-gray-700 border border-gray-600 text-white rounded-md h-8 text-sm px-2"
                    data-testid="unit-overhead-position-select"
                  >
                    <option value="">Select position</option>
                    {positions.map(p => <option key={p} value={p}>{p}</option>)}
                  </select>
                </div>
              </div>

              {/* Row 3: Date/Time From, Date/Time To */}
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="text-gray-400 text-xs">Date/Time From (24 hour)</label>
                  <div className="flex gap-1">
                    <Input
                      type="date"
                      value={form.exercise_start_date}
                      onChange={(e) => setForm(prev => ({ ...prev, exercise_start_date: e.target.value }))}
                      className="bg-gray-700 border-gray-600 text-white h-8 text-sm flex-1"
                      data-testid="unit-start-date-input"
                    />
                    <Input
                      type="text"
                      placeholder="HH:MM"
                      maxLength="5"
                      value={form.exercise_start_time || ''}
                      onChange={(e) => handleTimeInput(e.target.value, 'exercise_start_time')}
                      className="bg-gray-700 border-gray-600 text-white h-8 text-sm w-20"
                      data-testid="unit-start-time-input"
                    />
                  </div>
                </div>
                <div>
                  <label className="text-gray-400 text-xs">Date/Time To (24 hour)</label>
                  <div className="flex gap-1">
                    <Input
                      type="date"
                      value={form.incident_release_date}
                      onChange={(e) => setForm(prev => ({ ...prev, incident_release_date: e.target.value }))}
                      className="bg-gray-700 border-gray-600 text-white h-8 text-sm flex-1"
                      data-testid="unit-release-date-input"
                    />
                    <Input
                      type="text"
                      placeholder="HH:MM"
                      maxLength="5"
                      value={form.incident_release_time}
                      onChange={(e) => handleTimeInput(e.target.value, 'incident_release_time')}
                      className="bg-gray-700 border-gray-600 text-white h-8 text-sm w-20"
                      data-testid="unit-release-time-input"
                    />
                  </div>
                </div>
              </div>

              {/* Row 4: Requested Arrival Date/Time, Assigned Exercise */}
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="text-gray-400 text-xs">Requested Arrival Date/Time (24 hour)</label>
                  <div className="flex gap-1">
                    <Input
                      type="date"
                      value={form.requested_arr_datetime ? form.requested_arr_datetime.split('T')[0] : ''}
                      onChange={(e) => {
                        const date = e.target.value;
                        const time = form.requested_arr_datetime ? form.requested_arr_datetime.split('T')[1] : '';
                        setForm(prev => ({ ...prev, requested_arr_datetime: `${date}T${time}` }));
                      }}
                      className="bg-gray-700 border-gray-600 text-white h-8 text-sm flex-1"
                      data-testid="unit-arr-date-input"
                    />
                    <Input
                      type="text"
                      placeholder="HH:MM"
                      maxLength="5"
                      value={form.requested_arr_datetime ? form.requested_arr_datetime.split('T')[1] || '' : ''}
                      onChange={(e) => {
                        let v = e.target.value.replace(/[^0-9:]/g, '');
                        if (v.length === 2 && !v.includes(':')) v = v + ':';
                        if (v.length <= 5) {
                          const date = form.requested_arr_datetime ? form.requested_arr_datetime.split('T')[0] : '';
                          setForm(prev => ({ ...prev, requested_arr_datetime: `${date}T${v}` }));
                        }
                      }}
                      className="bg-gray-700 border-gray-600 text-white h-8 text-sm w-20"
                      data-testid="unit-arr-time-input"
                    />
                  </div>
                </div>
                <div>
                  <label className="text-gray-400 text-xs">Assigned Exercise</label>
                  <select
                    value={form.assigned_exercise || ''}
                    onChange={(e) => setForm(prev => ({ ...prev, assigned_exercise: e.target.value }))}
                    className="w-full bg-gray-700 border border-gray-600 text-white rounded-md h-8 text-sm px-2"
                    data-testid="unit-exercise-select"
                  >
                    <option value="">None</option>
                    {exercises.map(ex => <option key={ex.id} value={ex.id}>{ex.exercise_name}</option>)}
                  </select>
                </div>
              </div>

              {/* Row 5: Situation Summary */}
              <div>
                <label className="text-gray-400 text-xs">Situation Summary</label>
                <Textarea
                  value={form.description}
                  onChange={(e) => setForm(prev => ({ ...prev, description: e.target.value }))}
                  placeholder="Description of the unit's situation..."
                  rows={2}
                  className="bg-gray-700 border-gray-600 text-white text-sm"
                  data-testid="unit-description-input"
                />
              </div>

              {/* Row 6: Work Assignment, Special Equip */}
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="text-gray-400 text-xs">Work Assignment & Special Inst</label>
                  <Textarea
                    value={form.work_assignment_special_inst}
                    onChange={(e) => setForm(prev => ({ ...prev, work_assignment_special_inst: e.target.value }))}
                    placeholder="Work assignment details..."
                    rows={2}
                    className="bg-gray-700 border-gray-600 text-white text-sm"
                    data-testid="unit-work-assignment-input"
                  />
                </div>
                <div>
                  <label className="text-gray-400 text-xs">Special Equip & Supplies</label>
                  <Textarea
                    value={form.special_equip_supplies}
                    onChange={(e) => setForm(prev => ({ ...prev, special_equip_supplies: e.target.value }))}
                    placeholder="Special equipment and supplies..."
                    rows={2}
                    className="bg-gray-700 border-gray-600 text-white text-sm"
                    data-testid="unit-special-equip-input"
                  />
                </div>
              </div>

              {/* Row 7: Notes */}
              <div>
                <label className="text-gray-400 text-xs">Notes</label>
                <Input
                  value={form.notes}
                  onChange={(e) => setForm(prev => ({ ...prev, notes: e.target.value }))}
                  placeholder="Optional notes"
                  className="bg-gray-700 border-gray-600 text-white h-8 text-sm"
                />
              </div>

              {/* Action buttons */}
              <div className="flex gap-2 pt-1">
                <Button onClick={handleSave} size="sm" className="bg-orange-500 hover:bg-orange-600 h-7 text-xs" data-testid="save-unit-btn">
                  <Save className="h-3 w-3 mr-1" />{editingUnit ? 'Update' : 'Add'} Unit
                </Button>
                <Button onClick={resetForm} variant="ghost" size="sm" className="text-gray-400 h-7 text-xs">
                  <X className="h-3 w-3 mr-1" />Cancel
                </Button>
              </div>
            </div>
          ) : (
            <button
              onClick={() => setShowForm(true)}
              className="ml-4 flex items-center gap-1.5 text-xs text-orange-400 hover:text-orange-300 transition-colors py-1"
              data-testid={`add-unit-${resourceId}`}
            >
              <Plus className="h-3.5 w-3.5" />Add Unit
            </button>
          )}
        </div>
      )}

      {/* Tracker Link Modal */}
      {trackerModal && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center" onClick={() => setTrackerModal(null)}>
          <div className="bg-gray-800 border border-gray-700 rounded-xl p-6 max-w-md w-full mx-4 shadow-xl" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-white font-semibold flex items-center gap-2">
                <MapPin className="h-5 w-5 text-green-400" />
                Unit Tracker
              </h3>
              <button onClick={() => setTrackerModal(null)} className="text-gray-400 hover:text-white">
                <X className="h-5 w-5" />
              </button>
            </div>
            <p className="text-gray-400 text-sm mb-4">
              Share this link, code, or QR with the operator of <span className="text-orange-400 font-medium">{trackerModal.name}</span> to enable GPS tracking.
            </p>
            <div className="space-y-3">
              {/* QR Code */}
              <div className="flex justify-center py-2">
                <div className="bg-white p-3 rounded-lg">
                  <QRCodeSVG
                    value={trackerModal.link}
                    size={160}
                    level="M"
                    includeMargin={false}
                  />
                </div>
              </div>
              <div>
                <label className="text-gray-500 text-xs mb-1 block">Tracking Code</label>
                <div className="flex items-center gap-2">
                  <code className="flex-1 bg-gray-900 text-green-400 font-mono text-lg px-4 py-2 rounded-lg tracking-widest text-center">
                    {trackerModal.code}
                  </code>
                  <button
                    onClick={() => copyToClipboard(trackerModal.code, 'code')}
                    className="p-2 text-gray-400 hover:text-white bg-gray-700 rounded-lg"
                  >
                    {copiedField === 'code' ? <Check className="h-4 w-4 text-green-400" /> : <Copy className="h-4 w-4" />}
                  </button>
                </div>
              </div>
              <div>
                <label className="text-gray-500 text-xs mb-1 block">Tracker Link</label>
                <div className="flex items-center gap-2">
                  <input
                    readOnly
                    value={trackerModal.link}
                    className="flex-1 bg-gray-900 text-blue-400 text-sm px-3 py-2 rounded-lg font-mono"
                  />
                  <button
                    onClick={() => copyToClipboard(trackerModal.link, 'link')}
                    className="p-2 text-gray-400 hover:text-white bg-gray-700 rounded-lg"
                  >
                    {copiedField === 'link' ? <Check className="h-4 w-4 text-green-400" /> : <Copy className="h-4 w-4" />}
                  </button>
                </div>
              </div>
            </div>
            <p className="text-gray-500 text-xs mt-4">The operator opens this link on their phone to start transmitting GPS location.</p>
          </div>
        </div>
      )}
    </div>
  );
};

export default ResourceUnits;
